import type { ReactNode, RefObject } from 'react';
import { ArrowDown } from 'lucide-react';
import { LiksChatMessageList } from '../../components/liks/LiksChatMessageList/LiksChatMessageList';
import { KnowledgeDocumentPreviewModal } from '../../features/knowledge/components/KnowledgeDocumentPreviewModal';
import { useI18n } from '../../i18n/useI18n';
import type { ChatMessage, ChatMessageSource } from '../../types/chatMessage';
import { VISITOR_AUTHOR_ID } from './chatbotConversationMessageMapping';

type KnowledgeBaseOption = {
  id: string;
  name: string;
};

type ChatbotConversationTranscriptProps = {
  messages: ChatMessage[];
  messagesRef: RefObject<HTMLDivElement | null>;
  messagesScrollbarClassName: string;
  onMessagesScroll: () => void;
  messageListHeader: ReactNode;
  isAtBottom: boolean;
  onScrollToBottom: () => void;
  chatbotKnowledgeBaseId: string;
  accessibleKnowledgeBases: KnowledgeBaseOption[] | undefined;
  canImproveMessage: (message: ChatMessage) => boolean;
  onImproveMessage: (messageId: string) => void;
  onOpenTrainingExample: (source: ChatMessageSource) => void;
};

export function ChatbotConversationTranscript({
  messages,
  messagesRef,
  messagesScrollbarClassName,
  onMessagesScroll,
  messageListHeader,
  isAtBottom,
  onScrollToBottom,
  chatbotKnowledgeBaseId,
  accessibleKnowledgeBases,
  canImproveMessage,
  onImproveMessage,
  onOpenTrainingExample,
}: ChatbotConversationTranscriptProps) {
  const { locale, translate } = useI18n();

  return (
    <div className="platform-chat-page__messages-wrap">
      <div
        ref={messagesRef}
        className={`platform-chat-page__messages ${messagesScrollbarClassName}`}
        onScroll={onMessagesScroll}
      >
        <LiksChatMessageList
          messages={messages}
          header={messageListHeader}
          stickToBottom={isAtBottom}
          currentUserId={VISITOR_AUTHOR_ID}
          locale={locale}
          copyLabel={translate('platformChat.copyMessage')}
          copiedLabel={translate('platformChat.copiedMessage')}
          retryLabel={translate('platformChat.retry')}
          downloadImageLabel={translate('platformChat.downloadImage')}
          downloadAttachmentLabel={translate('platformChat.downloadAttachment')}
          expandImageLabel={translate('platformChat.expandImage')}
          closeImagePreviewLabel={translate('platformChat.closeImagePreview')}
          imageLoadingLabel={translate('platformChat.imageLoading')}
          assistantReplyingLabel={translate('platformChat.assistantReplying')}
          generatedImageAltLabel={translate('platformChat.generatedImageAlt')}
          sourcesLabel={translate('platformChat.sources')}
          sourcesEmptyLabel={translate('platformChat.sourcesEmpty')}
          sourceTypeColumnLabel={translate('platformChat.sourceTable.type')}
          sourceFileColumnLabel={translate('platformChat.sourceTable.file')}
          sourceUrlColumnLabel={translate('platformChat.sourceTable.url')}
          sourceInaccessibleLabel={translate('platformChat.sourceInaccessible')}
          sourceTrainingDeletedLabel={translate(
            'platformChat.sourceTrainingDeleted',
          )}
          getSourceTypeLabel={(sourceType) => {
            if (
              sourceType === 'KDB_DOCUMENT' ||
              sourceType === 'CRAWL' ||
              sourceType === 'INTERNET' ||
              sourceType === 'TRAINING_EXAMPLE'
            ) {
              return translate(`platformChat.sourceTypes.${sourceType}`);
            }
            return translate('platformChat.sourceTypes.unknown');
          }}
          openDocumentLabel={translate('platformChat.openDocument')}
          openTrainingExampleLabel={translate(
            'platformChat.openTrainingExample',
          )}
          documentPreviewCloseLabel={translate(
            'platformChat.closeDocumentPreview',
          )}
          documentPreviewLoadingLabel={translate(
            'platformChat.documentPreviewLoading',
          )}
          documentPreviewErrorLabel={translate(
            'platformChat.documentPreviewError',
          )}
          documentPreviewInaccessibleLabel={translate(
            'platformChat.documentPreviewInaccessible',
          )}
          documentPreviewUnavailableLabel={translate(
            'platformChat.documentPreviewUnavailable',
          )}
          renderDocumentPreview={(props) => (
            <KnowledgeDocumentPreviewModal {...props} />
          )}
          enableRetry={false}
          enableAttachments={false}
          fallbackKnowledgeBaseId={
            chatbotKnowledgeBaseId !== '' ? chatbotKnowledgeBaseId : undefined
          }
          knowledgeBases={accessibleKnowledgeBases}
          onOpenTrainingExample={onOpenTrainingExample}
          improveLabel={translate('chatbots.conversations.detail.improve')}
          canImproveMessage={canImproveMessage}
          onImproveMessage={onImproveMessage}
        />
      </div>

      {!isAtBottom && (
        <button
          type="button"
          className="platform-chat-page__scroll-to-bottom"
          aria-label={translate('platformChat.scrollToBottom')}
          onClick={onScrollToBottom}
        >
          <ArrowDown
            className="platform-chat-page__scroll-to-bottom-icon"
            strokeWidth={2}
            aria-hidden
          />
        </button>
      )}
    </div>
  );
}
